import { Plural, t, Trans } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import type { AlbumInfoStore } from '../../../store/AlbumInfoStore'
import { AlbumMembers, avatarSize } from './AlbumMembers'
import { AlbumTitle } from './AlbumTitle'
import { useBreakpointIndex } from '@jotta/ui/useBreakpointIndex'
import { cn } from '@jotta/utils/css'
import { CollectionType } from '@jotta/types/schemas/AlbumSchema'
import { Avatar } from '@jotta/ui/Avatar'
import { useNavigate } from 'react-router-dom'

export const AlbumMeta = observer<{
  store: AlbumInfoStore
}>(function AlbumInfo({ store }) {
  const { model: album, captureDate, total: photoCount } = store
  const { hasMorePhotos } = album
  return (
    <div className="body-sm-ln flex gap-2 text-grey4">
      {captureDate && (
        <>
          <span>{captureDate}</span>
          <span>•</span>
        </>
      )}
      {photoCount ? (
        <span>
          <Plural value={photoCount || 0} one="# photo" other="# photos" />
        </span>
      ) : (
        <span>
          <Trans id="AlbumContainer.emptyAlbum" />
        </span>
      )}
      {hasMorePhotos && <span>+</span>}
    </div>
  )
})

export const SharedPhotosInfo = observer<{
  store: AlbumInfoStore
  goBig: boolean
}>(function AlbumInfo({ store, goBig }) {
  const { model: album, photos } = store
  const { coverPhoto, ownerAvatar, shareInfo } = album
  const shareOwnerName = shareInfo?.ownerFullName

  const navigate = useNavigate()

  const photoStack = [
    photos
      .filter(value => value.id !== coverPhoto?.id)
      .slice(0, 1)
      .at(0),
    coverPhoto,
  ].filter(Boolean)

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4 md:grid-cols-[400px_auto] md:gap-8',
        {
          'md:grid-cols-[65fr_35fr]': goBig,
        },
      )}
    >
      <div className="md:pt-16">
        {ownerAvatar && <Avatar size={avatarSize} avatar={ownerAvatar} />}
        <header className="h2 py-2">
          <Trans>Shared by {shareOwnerName}</Trans>
        </header>

        <AlbumMeta store={store} />
      </div>

      <div className="relative aspect-[4/3] w-full md:order-first">
        {photoStack.map((photo, index, array) => (
          <img
            key={photo?.id}
            className={cn(
              'absolute top-0 size-full cursor-pointer rounded-lg object-cover',
            )}
            style={{
              rotate: `${index * -3}deg`,
            }}
            src={`${photo?.thumbnail_url}.l`}
            onClick={() => navigate(`./${photo?.id}`)}
          />
        ))}
      </div>
    </div>
  )
})

export const AlbumInfo = observer<{
  store: AlbumInfoStore
  goBig: boolean
}>(function AlbumInfo({ store, goBig }) {
  const { model: album } = store
  const { coverPhoto } = album
  const coverPhotoThumbnailUrl = coverPhoto?.thumbnail_url

  const largeView = useBreakpointIndex() > 1

  if (album.collectionType === CollectionType.SHARED_PHOTOS) {
    return <SharedPhotosInfo store={store} goBig={goBig} />
  }

  return (
    <div
      data-testid="AlbumView"
      className="grid grid-cols-1 items-center gap-4 md:grid-cols-[400px_auto] md:gap-8"
    >
      <div className="aspect-[4/3] w-full rounded-lg bg-grey3 p-2">
        <div className="size-full rounded-lg bg-white p-1.5">
          {coverPhotoThumbnailUrl && (
            <img
              className="size-full rounded-lg object-cover"
              src={`${coverPhotoThumbnailUrl}.l`}
            />
          )}
        </div>
      </div>

      <div className="flex grow flex-col items-start gap-2">
        <AlbumTitle store={store} />
        {largeView && <AlbumMeta store={store} />}
        <AlbumMembers store={store} />
      </div>
    </div>
  )
})
